import { getCatalogEditorModelPath } from "@/v2-backoffice-client/feature/configuration/feature.configuration.constants"
import type { JsonSchema } from "@/v2-backoffice-client/feature/configuration/feature.configuration.types"

/**
 * JSON Schema for a basemap's Configuration blob.
 *
 * Source of truth is the runtime's own parser — `BasemapConfiguration` in
 * `libs/backends/v2-layer-data` — which is what turns the stored JSON into
 * the basemap the console mounts. Keep this in step when that struct
 * changes: an unparseable blob does not degrade, it drops the basemap out
 * of the chooser entirely.
 */

const SPATIAL_REFERENCE: JsonSchema = {
  type: "object",
  description:
    "Coordinate system the basemap's tiles are published in. Every layer drawn on it is reprojected to this, so it is the map's spatial reference.",
  properties: {
    wkid: {
      type: "number",
      description:
        "Well-known id, e.g. 25833 (ETRS89 / UTM 33N) or 3857 (Web Mercator).",
    },
    latestWkid: { type: "number" },
    wkt: { type: "number" },
    xyTolerance: { type: "number" },
    zTolerance: { type: "number" },
    mTolerance: { type: "number" },
    xyUnits: { type: "number" },
  },
  required: ["wkid"],
}

const BASE_LAYER: JsonSchema = {
  type: "object",
  description: "One tile service drawn as part of this basemap.",
  properties: {
    type: {
      type: "string",
      description: "The ArcGIS layer class the console constructs.",
      enum: ["tile", "vector-tile", "wmts", "wms", "web-tile", "base-tile"],
    },
    url: {
      type: "string",
      description: "Service endpoint the tiles are requested from.",
    },
    tileInfo: {
      type: "object",
      description:
        "Tiling scheme (lods, origin, tile size). Required for services that do not advertise their own.",
    },
    fullExtent: {
      type: "object",
      description: "Extent the service has tiles for.",
    },
    serviceMode: {
      type: "string",
      description: "WMTS delivery mode.",
      enum: ["KVP", "RESTful"],
    },
    activeLayer: {
      type: "object",
      description: "WMTS: which published layer of the service to draw.",
      properties: { id: { type: "string" } },
      required: ["id"],
    },
  },
  required: ["type"],
}

const VIEWPOINT: JsonSchema = {
  type: "object",
  description:
    "Where the map lands when this basemap is chosen. Omit to keep the current view.",
  properties: {
    rotation: { type: "number" },
    scale: { type: "number" },
    targetGeometry: {
      type: "object",
      description: "Point or extent the view centres on.",
    },
    camera: {
      type: "object",
      description: "3D camera; only meaningful in a scene view.",
      properties: {
        tilt: { type: "number" },
        heading: { type: "number" },
        fov: { type: "number" },
        position: { type: "object" },
      },
    },
  },
}

const CONSTRAINTS: JsonSchema = {
  type: "object",
  description: "Limits the view while this basemap is active.",
  properties: {
    snapToZoom: {
      type: "boolean",
      description: "Snap to the tiling scheme's discrete zoom levels.",
    },
    minScale: {
      type: "number",
      description: "Furthest the operator can zoom out (larger = further).",
    },
    geometry: {
      type: "object",
      description: "Extent the view is confined to.",
    },
  },
}

const BASEMAP_CONFIGURATION_SCHEMA: JsonSchema = {
  $schema: "http://json-schema.org/draft-07/schema#",
  type: "object",
  title: "Basemap configuration",
  properties: {
    Properties: {
      type: "object",
      description: "What the basemap is made of.",
      properties: {
        spatialReference: SPATIAL_REFERENCE,
        thumbnail: {
          type: "string",
          description:
            "Image shown in the console's basemap chooser. Read straight out of here on every publish — the Thumbnail column is derived from it, never typed separately.",
        },
        baseLayers: {
          type: "array",
          description: "Drawn bottom-first, in order.",
          items: BASE_LAYER,
          minItems: 1,
        },
      },
      required: ["spatialReference", "baseLayers"],
    },
    DefaultViewpoint: VIEWPOINT,
    Constraints: CONSTRAINTS,
  },
  required: ["Properties"],
}

const CONFIGURATION_MODEL_NAME = "basemap.configuration.json"

/** The basemap catalog's contribution to the shared Monaco registry. */
export const BASEMAP_EDITOR_SCHEMAS: Record<string, JsonSchema> = {
  [CONFIGURATION_MODEL_NAME]: BASEMAP_CONFIGURATION_SCHEMA,
}

export function getBasemapEditorModelPath(): string {
  return getCatalogEditorModelPath(CONFIGURATION_MODEL_NAME)
}

/**
 * What a new basemap's editor is seeded with: the required skeleton, so the
 * operator fills in a shape the runtime can already parse rather than
 * starting from an empty object the schema immediately flags.
 */
export const BASEMAP_CREATE_CONFIGURATION = JSON.stringify(
  {
    Properties: {
      spatialReference: { wkid: 25833 },
      thumbnail: "",
      baseLayers: [{ type: "tile", url: "" }],
    },
  },
  null,
  2,
)
